import { Box } from '@mui/material';
import { ActionButton, useModeTokens } from '@/common/components/ui';
import { Headline, Eyebrow } from './primitives';
import { ANCHOR } from './content';

const QUESTIONS = [
  [
    'Do I need to change how I make music?',
    'Keep working in your DAW. Add your existing project folder to StudioVault and create a version when you want a checkpoint. You can upload stem packs too.',
  ],
  [
    'Does every save automatically become a version?',
    'No. You create versions in StudioVault with a commit message. Sync checks help you review what changed before uploading.',
  ],
  [
    'Can I get back an older version?',
    'Yes. Open Versions on a project and use Download to choose a destination for that version, or Sync to apply it to your linked folder. Sync shows a confirmation because newer local files can be overwritten and local-only files can be deleted.',
  ],
  [
    'What does the desktop app do?',
    'It connects StudioVault to folders on your computer for local file checks, version creation, sync and opening sessions in your installed DAW. You can also browse and organize projects and boards on the web. The download dialog lists currently available releases.',
  ],
  [
    'How many collaborators can I add?',
    'As many as you want. There is no limit on the number of collaborators you can add to a project. Invite your whole team and choose collaborator or read-only access for each person.',
  ],
  [
    'What can I share with other people?',
    'Invite people to a project as collaborators or read-only members. Collaborators can contribute versions; read-only members can view and download files. Board invitations are separate from project access, so share the underlying projects with people who need them.',
  ],
  [
    'Are my plugins included in a project?',
    'No. Collaborators still need their own plugins and licenses. The experimental Plugins tab can help identify plugins used in a version, but detection may be incomplete or inaccurate.',
  ],
];

export function LandingFaq({ onDownload }: { onDownload: () => void }) {
  const t = useModeTokens();
  return (
    <Box
      component="section"
      id="lp-faq"
      sx={{
        ...ANCHOR,
        px: { xs: '20px', md: '40px' },
        py: { xs: '44px', md: '70px' },
        display: 'grid',
        gridTemplateColumns: {
          xs: 'minmax(0,1fr)',
          md: 'minmax(0,.65fr) minmax(0,1fr)',
        },
        gap: { xs: 3, md: 8 },
      }}
    >
      <Box>
        <Eyebrow mb="16px">BEFORE THE FIRST SESSION</Eyebrow>
        <Headline min={30} max={42}>
          A few things
          <br />
          worth knowing.
        </Headline>
        <Box
          component="p"
          sx={{
            color: t.text.secondary,
            fontSize: 14,
            lineHeight: 1.65,
            my: 3,
          }}
        >
          Start with a project you know. Create a checkpoint, invite a
          collaborator and see where the next idea takes you.
        </Box>
        <ActionButton variant="secondary" onClick={onDownload}>
          Find your desktop download ↗
        </ActionButton>
      </Box>
      <Box>
        {QUESTIONS.map(([question, answer]) => (
          <Box
            component="details"
            className="lp-faq"
            key={question}
            sx={{ borderBottom: '1px solid', borderColor: t.borders.subtle }}
          >
            <summary>{question}</summary>
            <Box
              component="p"
              sx={{
                m: '0 0 22px',
                color: t.text.secondary,
                fontSize: 13,
                lineHeight: 1.75,
              }}
            >
              {answer}
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
}
